import { OutputAction, trackOutputAction } from 'analytics';
import { putTextOnClipboard } from 'clipboard';
import Icon, { IconType } from 'components/Icon';
import { useCallback, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { unthemed } from 'tokens';
import Modal from '../..';
import * as ModalStyles from '../../styles';
import AccessManagerModal from '../AccessManagerModal';
import * as S from '../styles';

interface ShareEntityModalConfig {
	title: string;
	entityLabel: string;
	editorsOnlyLabel: string;
	publiclyReadable: boolean;
	showPrivateOption: boolean;
	isPubliclyReadableForced: boolean;
	canManageAccess: boolean;
	shareUrl: string;
	shareTitle?: string;
	canNativeShare: boolean;
	trackAction: OutputAction;
	onAccessChange: (publiclyReadable: boolean) => Promise<void>;
	accessManagerProps: Omit<
		React.ComponentProps<typeof AccessManagerModal>,
		'onBack'
	>;
	onClose: () => void;
}

interface ShareEntityModalProps {
	config: ShareEntityModalConfig;
}

const ShareEntityModal = ({ config }: ShareEntityModalProps) => {
	const theme = useTheme();
	const [showManageAccess, setShowManageAccess] = useState(false);
	const [publiclyReadable, setPubliclyReadable] = useState(config.publiclyReadable);

	const { isMobile } = useSelector((r: RootReducer) => {
		return { isMobile: selectIsMobile(r.mode) };
	}, shallowEqual);

	const close = useCallback(() => {
		config.onClose();
	}, [config]);

	const onAccessChange = useCallback(
		async (newPubliclyReadable: boolean) => {
			await config.onAccessChange(newPubliclyReadable);
			setPubliclyReadable(newPubliclyReadable);
		},
		[config]
	);

	const onCopyLink = useCallback(async () => {
		const url = config.shareUrl;

		if (config.canNativeShare) {
			try {
				await navigator.share({
					title: config.shareTitle ?? config.entityLabel,
					url,
				});
				trackOutputAction(config.trackAction);
				return;
			} catch (error) {
				if ((error as DOMException)?.name === 'AbortError') {
					return;
				}
				console.warn('Native share failed, falling back to copy', error);
			}
		}

		try {
			await putTextOnClipboard(url);
			trackOutputAction(config.trackAction);
			toast.success('Link copied');
		} catch (error) {
			toast.error('Copy failed — please copy manually');
			console.error('Failed to copy link to clipboard', error);
		}
	}, [config]);

	const linkLabel = config.canNativeShare
		? `Share ${config.entityLabel} link`
		: `Copy ${config.entityLabel} link`;

	if (showManageAccess) {
		return (
			<AccessManagerModal
				title={config.accessManagerProps.title}
				onBack={() => setShowManageAccess(false)}
				onClose={close}
				userAccess={config.accessManagerProps.userAccess}
				organizationAccess={config.accessManagerProps.organizationAccess}
				copyLink={config.accessManagerProps.copyLink}
			/>
		);
	}

	return (
		<Modal title={config.title} cancelLabel="Close" submitLabel="Save" onClose={close}>
			<>
				<S.AccessSection>
					<ModalStyles.SectionTitle>Access Control</ModalStyles.SectionTitle>
					<S.ToggleContainer>
						{config.showPrivateOption && (
							<S.ToggleButton
								selected={!publiclyReadable}
								onClick={() => onAccessChange(false)}
							>
								<S.ToggleButtonContent>
									<S.ToggleButtonIcon>
										<Icon type={IconType.LOCK} color={theme.gray[1000]} width={16} height={16} />
									</S.ToggleButtonIcon>
									<S.ToggleButtonText>{config.editorsOnlyLabel}</S.ToggleButtonText>
								</S.ToggleButtonContent>
							</S.ToggleButton>
						)}
						<S.ToggleButton
							selected={publiclyReadable || config.isPubliclyReadableForced}
							onClick={() => onAccessChange(true)}
						>
							<S.ToggleButtonContent>
								<S.ToggleButtonIcon>
									<Icon type={IconType.GLOBE} color={theme.gray[1000]} width={16} height={16} />
								</S.ToggleButtonIcon>
								<S.ToggleButtonText>Anyone with the link can view</S.ToggleButtonText>
							</S.ToggleButtonContent>
						</S.ToggleButton>
					</S.ToggleContainer>
					{config.canManageAccess && !isMobile && (
						<S.ManageLinksContainer>
							<S.ManageLink
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setShowManageAccess(true);
								}}
							>
								{config.accessManagerProps.title}
							</S.ManageLink>
						</S.ManageLinksContainer>
					)}
				</S.AccessSection>
				<S.LinkSection>
					<S.LinkButton onClick={onCopyLink}>
						<Icon
							type={config.canNativeShare ? IconType.SHARE : IconType.COPY}
							color={unthemed.gray['0']}
							width={14}
							height={14}
						/>
						<S.Link>{linkLabel}</S.Link>
					</S.LinkButton>
				</S.LinkSection>
			</>
		</Modal>
	);
};

export default ShareEntityModal;
export type { ShareEntityModalConfig };
